import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  RadioGroup,
  Radio,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useNotify } from "react-admin";
import { rolesApi, userApi } from "../providers/env";
import { RoleOut, UserRead } from "../generated";

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserRead | null;
  organizationId: string;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  onSuccess,
  user,
  organizationId,
}) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [emailError, setEmailError] = useState<string>("");
  const [userLevel, setUserLevel] = useState<string>("user");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    role_id: "",
    is_active: true,
  });

  const currentUserIsSuperAdmin = (() => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        return userInfo.is_superuser || false;
      }
    } catch {
      return false;
    }
    return false;
  })();

  useEffect(() => {
    if (open && user) {
      // Determine user level from permissions (most reliable for old data)
      let level = "user";
      if (user.is_superuser) {
        level = "super_admin";
      } else {
        const perms = user.role?.permissions || [];
        const hasRoleManagement = perms.some(
          (p: any) => p.resource === "role" && ["create", "edit", "delete"].includes(p.action)
        );
        const hasUserManagement = perms.some(
          (p: any) => p.resource === "user" && ["create", "edit"].includes(p.action)
        );
        if (hasRoleManagement) {
          level = "super_admin";
        } else if (hasUserManagement) {
          level = "sub_admin";
        } else {
          level = "user";
        }
      }

      setUserLevel(level);
      setForm({
        full_name: (user as any).full_name || "",
        email: user.email || "",
        password: "",
        role_id: user.role_id || "",
        is_active: user.is_active ?? true,
      });
      setEmailError("");
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const orgId = user?.organization_id || organizationId;
      const params = orgId ? { organizationId: orgId } : {};
      const response = await rolesApi.getRoles(params);
      setRoles(response.data || []);
    } catch (error: any) {
      notify("Error loading roles", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      const emailValue = value as string;
      if (emailValue && !validateEmail(emailValue)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleUserLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const level = e.target.value;
    setUserLevel(level);

    // For super_admin, auto-select the super_admin role (if exists)
    if (level === "super_admin") {
      const superAdminRole = roles.find((r) => r.user_level?.toLowerCase() === "super_admin");
      if (superAdminRole) {
        setForm((prev) => ({ ...prev, role_id: superAdminRole.id }));
      } else {
        setForm((prev) => ({ ...prev, role_id: "" }));
      }
    } else {
      // Reset role selection when level changes
      setForm((prev) => ({ ...prev, role_id: "" }));
    }
  };

  // Detect role level from its user_level field OR permissions (handles old data)
  const getRoleLevel = (role: RoleOut): string => {
    const level = role.user_level?.toLowerCase();
    if (level === "super_admin" || level === "sub_admin") return level;

    // Fallback: check permissions for old data where user_level is wrong
    const perms = role.permissions || [];
    const hasRoleMgmt = perms.some(
      (p: any) => p.resource === "role" && ["create", "edit", "delete"].includes(p.action)
    );
    const hasUserMgmt = perms.some(
      (p: any) => p.resource === "user" && ["create", "edit"].includes(p.action)
    );
    if (hasRoleMgmt) return "super_admin";
    if (hasUserMgmt) return "sub_admin";
    return "user";
  };

  // Filter roles based on selected user level
  const getFilteredRoles = (): RoleOut[] => {
    if (!roles || roles.length === 0) return [];
    return roles.filter((role) => getRoleLevel(role) === userLevel);
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (form.email && !validateEmail(form.email)) {
      notify("Please enter a valid email address", { type: "warning" });
      setEmailError("Please enter a valid email address");
      return;
    }

    // Role validation (not for super_admin)
    if (userLevel !== "super_admin" && !form.role_id) {
      notify("Role is required", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      const updateData: any = {
        full_name: form.full_name || undefined,
        is_active: form.is_active,
      };

      // Set role based on user level
      if (userLevel === "super_admin") {
        // For super_admin, set is_superuser to true and use super_admin role if exists
        updateData.is_superuser = true;
        const superAdminRole = roles.find((r) => r.user_level?.toLowerCase() === "super_admin");
        if (superAdminRole) {
          updateData.role_id = superAdminRole.id;
        }
      } else {
        // For other levels, set is_superuser to false and use selected role
        updateData.is_superuser = false;
        updateData.role_id = form.role_id || undefined;
      }

      if (form.email && form.email !== user.email) {
        updateData.email = form.email;
      }

      if (form.password.trim()) {
        updateData.password = form.password;
      }

      await userApi.updateUser({
        userId: user.id,
        userUpdate: updateData,
      });

      notify("User updated successfully", { type: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error updating user", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <EditIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit User</Typography>
          <Typography variant="body2" color="text.secondary">Update user details</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <TextField
              margin="normal"
              label="Full Name"
              name="full_name"
              fullWidth
              value={form.full_name}
              onChange={handleChange}
              autoFocus
            />
            <TextField
              margin="normal"
              label="Email Address"
              name="email"
              type="email"
              fullWidth
              required
              value={form.email}
              onChange={handleChange}
              error={!!emailError}
              helperText={emailError || "Email can be updated if needed"}
            />
            {currentUserIsSuperAdmin && (
              <>
                <TextField
                  margin="normal"
                  label="New Password"
                  name="password"
                  type="password"
                  placeholder="Leave empty to keep current"
                  fullWidth
                  value={form.password}
                  onChange={handleChange}
                />
                <Alert severity="info" sx={{ mt: 1 }}>Leave password empty to keep current password.</Alert>
              </>
            )}

            {/* User Level Selection */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                User Level
              </Typography>
              <RadioGroup value={userLevel} onChange={handleUserLevelChange}>
                {currentUserIsSuperAdmin && (
                  <FormControlLabel
                    value="super_admin"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Org Admin{" "}
                          <Chip label="Highest Access" size="small" color="error" />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Full system access including organization and users management
                        </Typography>
                      </Box>
                    }
                  />
                )}
                <FormControlLabel
                  value="user"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        User{" "}
                        <Chip label="Standard Access" size="small" color="default" />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Regular user with limited permissions
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            {/* Role dropdown - hidden for super_admin */}
            {userLevel !== "super_admin" && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  name="role_id"
                  value={form.role_id}
                  onChange={handleChange as any}
                  label="Role"
                >
                  <MenuItem value="">
                    <em>Select role</em>
                  </MenuItem>
                  {getFilteredRoles().map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {userLevel === "super_admin" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Org Admin role will be automatically assigned with full system access.
              </Alert>
            )}

            <FormControlLabel
              sx={{ mt: 2 }}
              control={<Switch checked={form.is_active} onChange={handleSwitchChange} />}
              label={form.is_active ? "Active" : "Inactive"}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
